import React, { useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Input, Button, Space, Table, notification } from "antd";
import type { GetRef, TableColumnsType, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./item_select_packing_return.css";
import axios from "axios";

type InputRef = GetRef<typeof Input>;

interface DataType {
     key: React.Key;
     item_code: string;
     item_name: string;
     uom: string;
     location_rack: string;
     available_qty: number;
     demand_qty: number;
     supply_qty: number;
     pick_qty: number;
     packing_qty: number;
     return_qty_db?: number;
}

type DataIndex = keyof DataType;

interface ItemSelectProps {
     pickingListNo: string | null;
     onConfirm: (selectedItems: DataType[]) => void;
     onCancel: () => void;
}

const ItemSelectPackingReturn: React.FC<ItemSelectProps> = ({ pickingListNo, onConfirm, onCancel }) => {
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);
     const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
     const [data, setData] = useState<DataType[]>([]);
     const [isProgress, setIsProgress] = useState<boolean>(false);
     const backendUrl = process.env.REACT_APP_BACKEND_URL;

     useEffect(() => {
          if (pickingListNo) {
               const fetchData = async () => {
                    setIsProgress(true);
                    try {
                         const response = await axios.get(`${backendUrl}/api/picking-list/${pickingListNo}`);
                         const formattedData = response.data.map((item: any, idx: number) => ({ ...item, key: `${item.id}_${item.location_rack || ''}_${idx}` }));
                         setData(formattedData);
                         const itemCodeLocations: { [key: string]: Set<string> } = {};
                         formattedData.forEach((item: any) => {
                              if (!itemCodeLocations[item.item_code]) {
                                   itemCodeLocations[item.item_code] = new Set();
                              }
                              itemCodeLocations[item.item_code].add(item.location_rack || "");
                         });
                         const duplicateItemCodes = Object.keys(itemCodeLocations).filter(
                              (code) => itemCodeLocations[code].size > 1
                         );
                         const autoSelectedKeys = formattedData
                              .filter((item: any) => duplicateItemCodes.includes(item.item_code))
                              .map((item: any) => item.key);
                         setSelectedRowKeys(autoSelectedKeys);
                    } catch (error) {
                         console.error("Error fetching picking list details:", error);
                         notification.error({ message: "Failed to fetch item details." });
                    } finally {
                         setIsProgress(false);
                    }
               };
               fetchData();
          }
     }, [pickingListNo, backendUrl]);

     const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps["confirm"], dataIndex: DataIndex) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${dataIndex}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) => {
               const val = record[dataIndex];
               return val !== undefined && val !== null
                    ? val
                         .toString()
                         .toLowerCase()
                         .includes((value as string).toLowerCase())
                    : false;
          },
          onFilterDropdownOpenChange: (visible) => {
               if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
               }
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
          console.log("selectedRowKeys changed: ", newSelectedRowKeys);
          setSelectedRowKeys(newSelectedRowKeys);
     };

     const rowSelection = {
          selectedRowKeys,
          onChange: onSelectChange,
          getCheckboxProps: (record: DataType) => ({ disabled: Number(record.supply_qty || 0) <= 0, name: record.item_code }),
           hideSelectAll: true,
     };

     const columns: TableColumnsType<DataType> = [
          {
               title: "Item Code",
               dataIndex: "item_code",
               key: "item_code",
               ...getColumnSearchProps("item_code"),
               sorter: (a, b) => a.item_code.localeCompare(b.item_code),
               ellipsis: true,
          },
          {
               title: "Item Name",
               dataIndex: "item_name",
               key: "item_name",
               ...getColumnSearchProps("item_name"),
               sorter: (a, b) => a.item_name.localeCompare(b.item_name),
               ellipsis: true,
          },
          {
               title: "UOM",
               dataIndex: "uom",
               key: "uom",
               ...getColumnSearchProps("uom"),
               sorter: (a, b) => a.uom.localeCompare(b.uom),
               ellipsis: true,
          },
          {
               title: "Location",
               dataIndex: "location_rack",
               key: "location_rack",
               ...getColumnSearchProps("location_rack"),
               sorter: (a, b) => a.location_rack.localeCompare(b.location_rack),
               ellipsis: true,
          },
          {
               title: "Demand",
               dataIndex: "demand_qty",
               key: "demand_qty",
               sorter: (a, b) => Number(a.demand_qty || 0) - Number(b.demand_qty || 0),
               ellipsis: true,
          },
          {
               title: "Supply Qty",
               dataIndex: "supply_qty",
               key: "supply_qty",
               sorter: (a, b) => Number(a.supply_qty || 0) - Number(b.supply_qty || 0),
               ellipsis: true,
          },
     ];

     const handleConfirm = () => {
          const selectedItems = data
               .filter((item) => selectedRowKeys.includes(item.key))
               .map((item) => ({ ...item, return_qty: undefined }));
          onConfirm(selectedItems);
     };

     return (
          <div id="i02j" style={transformStyle}>
               <div className="gjs-grid-row" id="iat2">
                    <div className="gjs-grid-column" id="imde">
                         <Table
                              rowSelection={rowSelection}
                              columns={columns}
                              dataSource={data}
                              loading={isProgress}
                              pagination={{ pageSize: 5 }}
                         />
                    </div>
               </div>
               <div style={{ marginTop: "20px", textAlign: "right" }}>
                    <Space>
                         <Button onClick={onCancel} disabled={isProgress}>Cancel</Button>
                         <Button type="primary" onClick={handleConfirm} disabled={isProgress || selectedRowKeys.length === 0}>
                              Confirm
                         </Button>
                    </Space>
               </div>
          </div>
     );
};

export default ItemSelectPackingReturn;
